/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Event,
  EventEmitter,
  Element,
  State,
  Watch,
  getAssetPath,
  Method
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
import { MercuryBundles } from "@genexus/mercury";
import { config } from "../../common/config";
/* CUSTOM IMPORTS */
import { Locale } from "../../common/locale";
import { EntityData } from "../../common/types";
import {
  ActionListItemModelExtended,
  ActionListModel
} from "@genexus/chameleon-controls-library";
import {
  mapTypeCategoryDataToActionListModel,
  mapTypeDataToActionListModel
} from "./helpers";

import { sortActionListDefault } from "../../common/chameleon-helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/edit",
  "components/icon",
  "components/tooltip",
  "components/list-box",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];

@Component({
  tag: "gx-ide-new-object",
  styleUrl: "new-object.scss",
  shadow: { delegatesFocus: true },
  assetsDirs: ["gx-ide-assets/new-object"]
})
export class GxIdeNewObject {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/new-object/shortcuts.json`);

  @Element() el: HTMLGxIdeNewObjectElement;

  /* References needed to collect data on "Create" button submit */
  #categoriesActionListEl: HTMLChActionListRenderElement;
  #chShortcutsEl: HTMLChShortcutsElement;
  #descriptionEl!: HTMLChEditElement;
  #descriptionModifiedByUser = false;
  #moduleFolderEl!: HTMLGxIdeEntitySelectorElement;
  #nameEl!: HTMLChEditElement;
  #nameModifiedByUser = false;

  /**
   * Description of the new object
   */
  @State() description: string = undefined;

  /**
   * Name of the new object
   */
  @State() name: string = undefined;
  @State() nameValidationErrorMessage: string;
  @State() selectedCategory: TypeCategoryData | null;
  @State() selectedType: TypeData | null;
  // representations of TypeCategoryData[] as ComboBoxModel.
  @State() typeCategoriesActionListModel: ActionListModel = [];
  @State() typesActionListModel: ActionListModel = [];

  @State() selectedTypeMap: Map<string, string> = new Map();

  /**
   * Allows you to define the type selected by default when instantiating the dialog.
   * Represented by the type identifier.
   */
  @Prop() readonly defaultTypeId: string;

  /**
   * Default value for the Module/Folder field.
   */
  @Prop() readonly defaultParent: EntityData;

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Callback invoked when user wants to cancel object creation
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Callback invoked when user wants to confirm object creation
   */
  @Prop() readonly createCallback: CreateCallback;

  /**
   * Applies a shadow all around
   */
  @Prop() readonly shadow = false;

  /**
   * Array containing category list and its corresponding types
   */
  @Prop({ mutable: true }) typeCategories!: TypeCategoryData[];
  @Watch("typeCategories")
  typeCategoriesChanged(newValue: TypeCategoryData[]) {
    if (newValue?.length) {
      this.typeCategoriesActionListModel = mapTypeCategoryDataToActionListModel(
        this.typeCategories
      );
    }
  }

  /**
   * Current value for Module/Folder
   */
  @Prop() readonly parent: EntityData;

  /**
   * Callback invoked when the action is executed on the Module/Folder filter
   * (button '...'). It returns the information of the selected object (id and
   * name) or 'undefined' if it was canceled.
   */
  @Prop() readonly selectModuleCallback: SelectModuleCallback;

  /**
   * Callback invoked when you must validate a name. Returns an error message or
   * 'undefined' if the name is not valid.
   */
  @Prop() readonly validateNameCallback: ValidateNameCallback;

  /**
   * Callback to suggest name and description based on the given type.
   * Returns the suggested data or undefined if not applicable.
   */
  @Prop() readonly suggestCallback?: SuggestCallback;

  /**
   * This event emits the 'cancel' event
   */
  @Event() cancel: EventEmitter<boolean>;

  /**
   * This event emm=its the data needed to create a new object
   */
  @Event() create: EventEmitter<NewObjectData>;

  /**
   * This event is triggered after a validation of the object name is made.
   * Returns 'undefined' if it's valid, otherwise an error message.
   */
  @Event() validateName: EventEmitter<string | undefined>;

  connectedCallback() {
    // this.#validateInitialName();
  }

  async componentWillLoad() {
    await this.#init();
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.typeCategoriesChanged(this.typeCategories);
    this.#initializeSelectedTypeMap();
  }

  componentDidLoad() {
    this.#setSelectedCategoryOnActionList();
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  // - - - - - - - - - - -

  #cancelCallbackHandler = async (): Promise<void> => {
    await this.cancelCallback();
  };

  #categoryOnSelectionChangedHandler = (
    selectionEvent: CustomEvent<ActionListItemModelExtended[]>
  ): void => {
    const selection = selectionEvent.detail;
    if (selection.length > 0) {
      const selectedCategory = this.typeCategories.find(
        category => category.id === selection[0].item.id
      );
      if (selectedCategory) {
        this.selectedCategory = selectedCategory;
        if (selectedCategory.types.length > 0) {
          this.selectedType = selectedCategory.types.find(
            type => type.id === this.selectedTypeMap.get(selectedCategory.id)
          );
        }
      }
    }
  };

  #createCallbackHandler = async (): Promise<void> => {
    // validate name first
    const nameValidation: string | undefined = await this.validateNameCallback(
      this.#nameEl.value
    );
    if (!!nameValidation) {
      // name is invalid
      this.#validateNameHandler(nameValidation);
      return;
    }

    const data: NewObjectData = {
      typeId: this.selectedType?.id,
      name: this.#nameEl.value,
      description: !this.#descriptionModifiedByUser
        ? undefined
        : this.#descriptionEl.value,
      moduleId: this.#moduleFolderEl?.value?.id
    };
    const result = await this.createCallback(data);
    this.#validateNameHandler(result);
  };

  #descriptionValueHandler = () => {
    if (this.#nameModifiedByUser || this.#descriptionModifiedByUser) {
      return this.#descriptionEl.value;
    } else {
      // First try to get suggested description if callback exists
      if (this.suggestCallback) {
        return this.description ?? this.selectedType.name;
      }
      return this.selectedType.name;
    }
  };

  /**
   * Resolves the suggested name based on the context
   * @param selectedType The currently selected type
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #resolveNameSuggestion = async (selectedType: TypeData, suggestedName?: string) => {
    if (suggestedName) {
        return suggestedName.replace(/\s/g, "");
    }
    return selectedType.name.replace(/\s/g, "");
  };

  /**
   * Gets the suggested description based on the context
   * @param selectedType The currently selected type
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #resolveDescriptionSuggestion = async (selectedType: TypeData, suggestedDescription?: string) => {
    if (suggestedDescription) {
        return suggestedDescription;
    }
    return selectedType.name;
  };

  /**
   * Updates both name and description suggestions
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #updateSuggestions = async () => {
    if (!this.selectedType) {
      return;
    }

    if (this.#nameModifiedByUser && this.#descriptionModifiedByUser)
      return;

    const suggestions = await this.suggestCallback(this.selectedType.id);

    if (!this.#nameModifiedByUser) {
      this.name = await this.#resolveNameSuggestion(this.selectedType, suggestions.name);
    }

    if (!this.#descriptionModifiedByUser) {
      this.description = await this.#resolveDescriptionSuggestion(
        this.selectedType, 
        suggestions.description
      );
    }
  };

  /**
   * This method defines the initial selected category and type, and it then
   * initializes the name and description values.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #init = async () => {
    const defaultTypeFound = this.#setDefaultType(this.defaultTypeId);
    if (!defaultTypeFound) {
      this.selectedCategory = this.typeCategories[0];
      this.selectedType = this.selectedCategory.types[0];
    }

    await this.#updateSuggestions();
  };

  /**
   * Initializes the selectedTypeMap with the defaultTypeId if it exists, otherwise
   * it initializes the selectedTypeMap with the first type of each category.
   */
  // eslint-disable-next-line @stencil-community/own-methods-must-be-private
  #initializeSelectedTypeMap() {
    this.typeCategories.forEach(category => {
      if (this.defaultTypeId && category.id === this.selectedCategory.id) {
        this.selectedTypeMap.set(category.id, this.selectedType.id);
      } else {
        this.selectedTypeMap.set(category.id, category.types[0].id);
      }
    });
  }

  #onBlurNameHandler = async () => {
    if (this.#nameEl.value) {
      const result: string | undefined = await this.validateNameCallback(
        this.#nameEl.value
      );
      this.#validateNameHandler(result);
    }
  };

  #onInputDescriptionHandler = (e: CustomEvent<string>) => {
    this.description = e.detail;
    this.#descriptionModifiedByUser = true;
  };

  #onInputNameHandler = async (e: CustomEvent<any> | InputEvent) => {
    const name = e.detail;
    if (!this.#descriptionModifiedByUser) {
      // Description should be equal to name, unless the description was already
      // modified by the user.
      this.#descriptionEl.value = name;
    }
    this.#nameModifiedByUser = true;
  };

  #onNameValueChangedHandler = async (e: CustomEvent<string>) => {
    const name = e.detail;
    if (name) {
      const result: string | undefined = await this.validateNameCallback(name);
      this.#validateNameHandler(result);
    }
  };

  #renderModuleFolder = () => {
    if (this.selectedType.hasModule || this.selectedType.hasFolder) {
      let labelDescription = this.#componentLocale.moduleFolder;
      if (!this.selectedType.hasFolder) {
        labelDescription = this.#componentLocale.moduleOnly;
      } else if (!this.selectedType.hasModule) {
        labelDescription = this.#componentLocale.folderOnly;
      }
      return [
        <label
          // Module/folder
          class="label api-module-label"
          htmlFor="module"
        >
          {labelDescription}
        </label>,
        <gx-ide-entity-selector
          id="module"
          class="api-module-control"
          part="module"
          labelPosition="none"
          value={this.parent}
          defaultValue={this.defaultParent}
          selectEntityCallback={this.selectModuleCallback}
          ref={(el: HTMLGxIdeEntitySelectorElement) =>
            (this.#moduleFolderEl = el as HTMLGxIdeEntitySelectorElement)
          }
        ></gx-ide-entity-selector>
      ];
    }
    return null;
  };

  #setDefaultType = (typeId: string | undefined): boolean => {
    if (!typeId) {
      return false;
    }
    let found = false;
    outerLoop: for (let i = 0; i < this.typeCategories.length; i++) {
      for (let j = 0; j < this.typeCategories[i].types.length; j++) {
        const loopTypeId = this.typeCategories[i].types[j].id;
        if (loopTypeId === typeId) {
          this.selectedCategory = this.typeCategories[i];
          this.selectedType = this.typeCategories[i].types[j];
          found = true;
          break outerLoop;
        }
      }
    }
    return found;
  };

  #setSelectedCategoryOnActionList = () => {
    if (this.selectedCategory) {
      // update model by setting the selected category
      this.#categoriesActionListEl.updateItemProperties(
        this.selectedCategory.id,
        {
          selected: true,
          type: "actionable"
        }
      );
    }
  };

  #typeChangedHandler = async (
    selectionEvent: CustomEvent<ActionListItemModelExtended[]>
  ) => {
    const selection = selectionEvent.detail;
    if (selection.length > 0) {
      this.selectedType = this.selectedCategory.types.find(
        type => type.id === selection[0].item.id
      );
      this.selectedTypeMap.set(this.selectedCategory.id, this.selectedType.id);

      await this.#updateSuggestions();
    }
  };

  /**
   * If the parameter is undefined, it means the name is valid. Otherwise, if it
   * receives a string, it indicates that the name is not valid. The string is the
   * error message to display.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #validateNameHandler = (result: string | undefined): void => {
    this.nameValidationErrorMessage = result;
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="main">
          <section class="field-group categories-types-layout spacing-body-inline spacing-body-block-start">
            <div class="field field-block">
              <label class="label">
                {this.#componentLocale.categorySelectionTitle}
              </label>
              <ch-action-list-render
                // #select a category
                // TODO: The "scrollable" class is a WA since the action-list currently does not support scrollbar customization
                class="list-box"
                selection="single"
                model={this.typeCategoriesActionListModel}
                sortItemsCallback={sortActionListDefault}
                onSelectedItemsChange={this.#categoryOnSelectionChangedHandler}
                ref={(el: HTMLChActionListRenderElement) =>
                  (this.#categoriesActionListEl =
                    el as HTMLChActionListRenderElement)
                }
              ></ch-action-list-render>
            </div>

            <div class="field field-block">
              <label class="label">
                {this.selectedCategory.name
                  ? `${this.selectedCategory.name} ${
                      this.#componentLocale.typeSelectionTitleSuffix
                    }`
                  : this.#componentLocale.typeSelectionTitle}
              </label>
              <ch-action-list-render
                // #data management types
                // TODO: The "scrollable" class is a WA since the action-list currently does not support scrollbar customization
                class="list-box"
                selection="single"
                sortItemsCallback={sortActionListDefault}
                model={mapTypeDataToActionListModel(
                  this.selectedCategory.types,
                  this.selectedCategory.id,
                  this.selectedType.id
                )}
                onSelectedItemsChange={this.#typeChangedHandler}
              ></ch-action-list-render>
            </div>
          </section>

          <section class="api-description-layout field-group-justified-start spacing-body-inline">
            <h2
              // API Description
              class="text-align-center api-description-title subtitle-regular-xs	"
            >
              {this.selectedType?.description}
            </h2>

            <label
              // Name
              class="label api-name-label"
              htmlFor="name"
            >
              {this.#componentLocale.objectName}
            </label>
            <div>
              <ch-edit
                autoFocus
                class={{
                  "input": true,
                  "api-name-control": true,
                  "input-error": !!this.nameValidationErrorMessage
                }}
                placeholder="Name"
                value={this.name}
                ref={(el: HTMLChEditElement) =>
                  (this.#nameEl = el as HTMLChEditElement)
                }
                onChange={this.#onNameValueChangedHandler}
                onInput={this.#onInputNameHandler as any}
                onBlur={this.#onBlurNameHandler}
                part="name"
              ></ch-edit>
              {this.nameValidationErrorMessage && (
                <ch-tooltip
                  class="tooltip"
                  actionElement={this.#nameEl as unknown as HTMLButtonElement}
                  blockAlign={config.tooltipSettings.blockAlign}
                  inlineAlign={config.tooltipSettings.inlineAlign}
                  delay={config.tooltipSettings.delay}
                >
                  {this.nameValidationErrorMessage}
                </ch-tooltip>
              )}
            </div>

            <label
              // Description
              class="label api-description-label"
              htmlFor="description"
            >
              {this.#componentLocale.objectDescription}
            </label>
            <ch-edit
              autoFocus
              class="input api-description-control"
              placeholder="Description"
              value={this.#descriptionValueHandler()}
              // TODO: toolTip={config.tooltip}
              ref={(el: HTMLChEditElement) =>
                (this.#descriptionEl = el as HTMLChEditElement)
              }
              onInput={this.#onInputDescriptionHandler as any}
              part="description"
            ></ch-edit>

            {this.#renderModuleFolder()}
          </section>
          <footer class="footer dialog-footer-with-border spacing-body-inline spacing-body-block-end ">
            <div class="buttons-spacer">
              <button
                // cancel
                class="button-secondary"
                onClick={this.#cancelCallbackHandler}
                part="button button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>

              <button
                // create
                class="button-primary"
                onClick={this.#createCallbackHandler}
                disabled={!!this.nameValidationErrorMessage}
                part="button button-create"
              >
                {this.#componentLocale.footer.btnCreate}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export interface NewObjectData {
  typeId: string;
  name: string;
  description: string | undefined;
  moduleId: string;
}

export type ValidateNameCallback = (
  name: string
) => Promise<string | undefined>;

export type SelectModuleCallback = () => Promise<EntityData | undefined>;

export type CreateCallback = (
  data: NewObjectData
) => Promise<string | undefined>;

export type CancelCallback = () => Promise<void>;

export type TypeCategoryData = {
  id: string;
  name: string;
  icon: string;
  types: TypeData[];
};

export type TypeData = {
  id: string;
  name: string;
  icon: string;
  hasModule: boolean;
  hasFolder: boolean;
  description: string;
};

export type SuggestData = {
  name: string | undefined;
  description: string | undefined;
}

export type SuggestCallback = (typeId:string) => Promise<SuggestData | undefined>;